<template>
    <v-sheet class="pa-3 bg-transparent">
        <div class="eq-toolbar">
            <v-switch v-model="autoFetch" label="Auto update" color="primary" density="compact" hide-details />
            <v-btn size="small" variant="tonal" :loading="loading" @click="refresh">Refresh</v-btn>
            <span v-if="owner.name && !anonymizeMode" class="text-body-2">{{ owner.name }}</span>
        </div>
        <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mb-3">{{ loadError }}</v-alert>
        <v-alert v-else-if="!owner.items.length" type="info" variant="tonal" density="compact" class="mb-3">
            No character snapshot yet. Your equipment appears after you log in or change channel while funnycharts is running.
        </v-alert>
        <equip-board :items="owner.items" />
    </v-sheet>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import type { OwnerEquipment } from '@/items/itemIndex';
import { anonymizeMode } from '@/store';
import EquipBoard from './subComponents/EquipBoard.vue';

const POLL_MS = 3000;
const AUTO_FETCH_KEY = 'funnycharts.equipment.autoFetch';
const loadAutoFetch = (): boolean => {
    try { return localStorage.getItem(AUTO_FETCH_KEY) !== '0'; } catch { return true; }
};

// The local character's worn set from /api/item-index/equipment, with full
// item detail. Equipment isn't on the websocket, so this polls while auto
// update is on.
export default defineComponent({
    components: { EquipBoard },
    setup() {
        const owner = ref<OwnerEquipment>({ id: '', name: '', items: [] });
        const loading = ref(false);
        const loadError = ref('');
        const autoFetch = ref(loadAutoFetch());

        const refresh = async () => {
            loading.value = true;
            try {
                const r = await fetch('/api/item-index/equipment');
                if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
                owner.value = await r.json();
                loadError.value = '';
            } catch (e) {
                loadError.value = `Failed to load equipment: ${(e as Error).message}`;
            } finally {
                loading.value = false;
            }
        };

        let timer: ReturnType<typeof setInterval> | null = null;
        const stopPolling = () => {
            if (timer) clearInterval(timer);
            timer = null;
        };
        const startPolling = () => {
            stopPolling();
            if (autoFetch.value) timer = setInterval(refresh, POLL_MS);
        };
        watch(autoFetch, on => {
            try { localStorage.setItem(AUTO_FETCH_KEY, on ? '1' : '0'); } catch { /* ignore */ }
            startPolling();
        });
        onMounted(() => { void refresh(); startPolling(); });
        onBeforeUnmount(stopPolling);

        return { owner, loading, loadError, autoFetch, refresh, anonymizeMode };
    },
});
</script>

<style scoped>
.eq-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 8px; }
</style>
